import Parse from 'parse';

import { ROISpecification, StepTask } from '@telespot/domain';
import {
  AnalysisTopology,
  AssetTopology,
  FindingTopology,
  ParseMapper,
  PipelineStepTopology,
  PipelineTopology,
  SampleTopology,
} from '@telespot/infrastructure/parse';
import { Coordinates, MosaicMapper, Roi, RoiFinding } from '@analysis/domain';

export class TelespotRoiMapper extends ParseMapper {
  public static toDomain(item: Parse.Object): Roi[] {
    const items: Roi[] = [];

    const data = item.get(FindingTopology.DATA);
    const content = data?.content as ROISpecification[];
    const creatorId = item.get(FindingTopology.CREATOR_ID);
    const findingVersion = item.get(FindingTopology.VERSION);
    const assetId = item.get(FindingTopology.ANALYSIS)?.get(AnalysisTopology.ASSET)?.id;
    const analysisId = item.get(FindingTopology.ANALYSIS)?.id;
    const sampleId = item.get(FindingTopology.ANALYSIS)?.get(AnalysisTopology.SAMPLE)?.id;
    const findingId = item.get(FindingTopology.UUID);
    const findingVersionId = item.id;
    const findingType = item.get(FindingTopology.TYPE);
    const creatorEntity = item.get(FindingTopology.CREATOR_ENTITY);
    const pipelineStepId = item.get(FindingTopology.PIPELINE_STEP)?.id;
    const pipelineId = item.get(FindingTopology.PIPELINE_STEP)?.get(PipelineStepTopology.PIPELINE)?.id;
    const originFindingVersionId = item.get(FindingTopology.ORIGIN)?.id;

    for (const contentRoi of content ?? []) {
      const roiId = MosaicMapper.createSpatialKey({ ...(contentRoi as Coordinates), findingId });

      items.push({
        id: roiId,
        labels: new Map(Object.entries(contentRoi.labels)),
        coordinates: {
          x0: Number(contentRoi.x0),
          y0: Number(contentRoi.y0),
          x1: Number(contentRoi.x1),
          y1: Number(contentRoi.y1),
        },
        creatorId,
        findingId,
        findingVersion,
        findingVersionId,
        assetId,
        analysisId,
        sampleId,
        additionalLabelsIncluded: true,
        findingType,
        creatorEntity,
        pipelineStepId,
        pipelineId,
        originFindingVersionId,
      });
    }

    return items;
  }

  public static toRoiFinding(item: Parse.Object): RoiFinding | undefined {
    const assetId = item.get(FindingTopology.ANALYSIS)?.get(AnalysisTopology.ASSET)?.id;
    const analysisId = item.get(FindingTopology.ANALYSIS)?.id;
    const sampleId = item.get(FindingTopology.ANALYSIS)?.get(AnalysisTopology.SAMPLE)?.id;
    const findingId = item.get(FindingTopology.UUID);
    const pipelineStepId = item.get(FindingTopology.PIPELINE_STEP)?.id;
    const creatorId = item.get(FindingTopology.CREATOR_ID);

    if (!assetId || !analysisId || !sampleId || !findingId || !pipelineStepId || !creatorId) {
      return undefined;
    }

    return {
      assetId,
      sampleId,
      analysisId,
      findingId,
      findingVersionId: item.id,
      findingVersion: item.get(FindingTopology.VERSION),
      findingType: item.get(FindingTopology.TYPE),
      creatorId,
      creatorEntity: item.get(FindingTopology.CREATOR_ENTITY),
      pipelineStepId,
      pipelineId: item.get(FindingTopology.PIPELINE_STEP)?.get(PipelineStepTopology.PIPELINE)?.id ?? '',
      originFindingVersionId: item.get(FindingTopology.ORIGIN)?.id,
    };
  }

  public fromDomain(rois: Roi[], deleted: Roi[] = []): Parse.Object[] {
    const parseObjectMap: Record<string, Parse.Object> = {};
    const groupedRois: Record<string, Record<string, Roi[]>> = {};

    // Single pass: process all ROIs and group undefined ones simultaneously
    for (const roi of rois) {
      if (roi.findingId && roi.analysisId) {
        // Process ROIs with existing findings (original logic)
        const parseFinding = (parseObjectMap[roi.findingId] ??= this.parse.Object.fromJSON({
          className: FindingTopology.TABLE,
          // [ObjectTopology.ID]: roi.findingVersionId,
        }));

        const parseSample = this.subclasses.getSubclass(SampleTopology.TABLE).createWithoutData(roi.sampleId);
        const parseAsset = this.subclasses.getSubclass(AssetTopology.TABLE).createWithoutData(roi.assetId);
        const parsePipeline = this.subclasses.getSubclass(PipelineTopology.TABLE).createWithoutData(roi.pipelineId);

        const parseAnalysis = this.parse.Object.fromJSON({
          className: AnalysisTopology.TABLE,
          objectId: roi.analysisId,
          [AnalysisTopology.SAMPLE]: parseSample,
          [AnalysisTopology.ASSET]: parseAsset,
          [AnalysisTopology.PIPELINE]: parsePipeline,
        });

        const parsePipelineStep = this.subclasses
          .getSubclass(PipelineStepTopology.TABLE)
          .createWithoutData(roi.pipelineStepId);
        const parseOrigin = roi.originFindingVersionId
          ? this.subclasses.getSubclass(FindingTopology.TABLE).createWithoutData(roi.originFindingVersionId)
          : undefined;

        parseFinding.set({
          [FindingTopology.CREATOR_ID]: roi.creatorId,
          // [FindingTopology.VERSION]: roi.findingVersion,
          [FindingTopology.ANALYSIS]: parseAnalysis,
          [FindingTopology.UUID]: roi.findingId,
          [FindingTopology.TYPE]: roi.findingType,
          [FindingTopology.PIPELINE_STEP]: parsePipelineStep,
          [FindingTopology.CREATOR_ENTITY]: roi.creatorEntity,
          [FindingTopology.ORIGIN]: parseOrigin,
          [FindingTopology.DATA]: {
            content: [
              ...(parseFinding.get(FindingTopology.DATA)?.content ?? []),
              {
                x0: roi.coordinates.x0,
                y0: roi.coordinates.y0,
                x1: roi.coordinates.x1,
                y1: roi.coordinates.y1,
                labels: Object.fromEntries(roi.labels),
              },
            ],
          },
        });
      } else {
        // Group ROIs without findings by assetId and sampleId
        const analysisGroupKey = this.encodeObject({
          assetId: roi.assetId,
          sampleId: roi.sampleId,
          pipelineId: roi.pipelineId,
          creatorEntity: roi.creatorEntity,
          creatorId: roi.creatorId,
        });

        const findingGroupKey = this.encodeObject({
          analysisGroupKey,
          pipelineStepId: roi.pipelineStepId,
          findingType: roi.findingType,
        });

        const findingGroup = (groupedRois[analysisGroupKey] ??= {});
        const findingRois = (findingGroup[findingGroupKey] ??= []);

        findingRois.push(roi);
      }
    }

    // Create new analyses and findings for grouped ROIs
    for (const [analysisGroupKey, analysisGroup] of Object.entries(groupedRois)) {
      const { assetId, sampleId, pipelineId, creatorEntity, creatorId } = this.decodeObject(analysisGroupKey);

      // Create new analysis for this group
      const parseAnalysis = this.createNewAnalysis({
        assetId,
        sampleId,
        pipelineId,
        creatorId,
      });

      for (const [findingGroupKey, findingRois] of Object.entries(analysisGroup)) {
        const { pipelineStepId, findingType } = this.decodeObject(findingGroupKey);

        parseObjectMap[findingGroupKey] = this.createNewFinding({
          pipelineStepId,
          creatorId,
          creatorEntity,
          findingType: findingType as StepTask,
          rois: findingRois,
          parseAnalysis,
          originFindingVersionId: findingRois[0].originFindingVersionId,
        });
      }
    }

    const findingIdsWithMergedRois = new Set(Object.keys(parseObjectMap));
    for (const roi of deleted) {
      if (roi.findingId && roi.analysisId && !findingIdsWithMergedRois.has(roi.findingId)) {
        findingIdsWithMergedRois.add(roi.findingId);
        parseObjectMap[roi.findingId] = this.createEmptyFindingFromRoi(roi);
      }
    }

    return Object.values(parseObjectMap);
  }

  private createEmptyFindingFromRoi(roi: Roi): Parse.Object {
    const parseFinding = this.parse.Object.fromJSON({
      className: FindingTopology.TABLE,
    });

    const parseSample = this.subclasses.getSubclass(SampleTopology.TABLE).createWithoutData(roi.sampleId);
    const parseAsset = this.subclasses.getSubclass(AssetTopology.TABLE).createWithoutData(roi.assetId);
    const parsePipeline = this.subclasses.getSubclass(PipelineTopology.TABLE).createWithoutData(roi.pipelineId);

    const parseAnalysis = this.parse.Object.fromJSON({
      className: AnalysisTopology.TABLE,
      objectId: roi.analysisId,
      [AnalysisTopology.SAMPLE]: parseSample,
      [AnalysisTopology.ASSET]: parseAsset,
      [AnalysisTopology.PIPELINE]: parsePipeline,
    });

    const parsePipelineStep = this.subclasses
      .getSubclass(PipelineStepTopology.TABLE)
      .createWithoutData(roi.pipelineStepId);
    const parseOrigin = roi.originFindingVersionId
      ? this.subclasses.getSubclass(FindingTopology.TABLE).createWithoutData(roi.originFindingVersionId)
      : undefined;

    parseFinding.set({
      [FindingTopology.CREATOR_ID]: roi.creatorId,
      [FindingTopology.ANALYSIS]: parseAnalysis,
      [FindingTopology.UUID]: roi.findingId,
      [FindingTopology.TYPE]: roi.findingType,
      [FindingTopology.PIPELINE_STEP]: parsePipelineStep,
      [FindingTopology.CREATOR_ENTITY]: roi.creatorEntity,
      [FindingTopology.ORIGIN]: parseOrigin,
      [FindingTopology.DATA]: { content: [] },
    });

    return parseFinding;
  }

  // REVIEW: consider using another mapper
  private createNewAnalysis({
    assetId,
    sampleId,
    pipelineId,
    creatorId,
  }: {
    assetId: string;
    sampleId: string;
    pipelineId: string;
    creatorId: string;
  }): Parse.Object {
    const ParseSample = this.subclasses.getSubclass(SampleTopology.TABLE);
    const ParseAsset = this.subclasses.getSubclass(AssetTopology.TABLE);
    const ParsePipeline = this.subclasses.getSubclass(PipelineTopology.TABLE);

    const parseAnalysis = this.parse.Object.fromJSON({
      className: AnalysisTopology.TABLE,
    });

    parseAnalysis.set({
      [AnalysisTopology.SAMPLE]: ParseSample.createWithoutData(sampleId),
      [AnalysisTopology.ASSET]: ParseAsset.createWithoutData(assetId),
      [AnalysisTopology.PIPELINE]: ParsePipeline.createWithoutData(pipelineId),
      [AnalysisTopology.CREATED_BY]: this.parse.User.createWithoutData(creatorId),
    });

    return parseAnalysis;
  }

  /**
   * Creates a new Finding Parse object
   */
  private createNewFinding({
    pipelineStepId,
    creatorId,
    creatorEntity,
    findingType,
    rois,
    parseAnalysis,
    originFindingVersionId,
  }: {
    pipelineStepId: string;
    creatorId: string;
    creatorEntity: string;
    findingType: StepTask;
    rois: Roi[];
    parseAnalysis: Parse.Object;
    originFindingVersionId?: string;
  }): Parse.Object {
    const ParsePipelineStep = this.subclasses.getSubclass(PipelineStepTopology.TABLE);

    const parseFinding = this.parse.Object.fromJSON({
      className: FindingTopology.TABLE,
    });

    parseFinding.set({
      [FindingTopology.CREATOR_ID]: creatorId,
      [FindingTopology.CREATOR_ENTITY]: creatorEntity,
      [FindingTopology.ANALYSIS]: parseAnalysis,
      [FindingTopology.UUID]: rois[0].findingId,
      [FindingTopology.TYPE]: findingType,
      [FindingTopology.PIPELINE_STEP]: ParsePipelineStep.createWithoutData(pipelineStepId),
      [FindingTopology.DATA]: {
        content: rois.map((roi) => ({
          x0: roi.coordinates.x0,
          y0: roi.coordinates.y0,
          x1: roi.coordinates.x1,
          y1: roi.coordinates.y1,
          labels: Object.fromEntries(roi.labels),
        })),
      },
      [FindingTopology.ORIGIN]: originFindingVersionId
        ? this.subclasses.getSubclass(FindingTopology.TABLE).createWithoutData(originFindingVersionId)
        : undefined,
    });

    return parseFinding;
  }

  private encodeObject(object: Record<string, string>): string {
    return JSON.stringify(object);
  }

  private decodeObject(string: string): Record<string, string> {
    return JSON.parse(string);
  }
}
